/** Effective commission amounts for external shipment commission list/dashboard display. */

const num = (value) => parseFloat(value || 0);

export const effectiveAgentCommission = (rec) =>
  num(rec?.total_agent_commission ?? rec?.company_share_on_balance);

export const effectiveOwnerShare = (rec) =>
  num(rec?.total_owner_share ?? rec?.owner_commission_amount);

export const effectiveAgentRemit = (rec) => {
  const target = num(rec?.total_agent_remit_due ?? rec?.net_receivable_from_owner);
  const received = num(rec?.amount_received_from_agent ?? rec?.amount_received_from_owner);
  const remaining = num(rec?.remaining_remit_from_agent ?? rec?.remaining_collect);
  if (target > 0) return target;
  return received + remaining;
};

export const effectiveOwnerPayable = (rec) => {
  const target = num(rec?.total_owner_share_due ?? rec?.net_payable_to_owner);
  const paid = num(rec?.amount_paid_to_owner);
  const remaining = num(rec?.remaining_pay_to_owner);
  if (target > 0) return target;
  return paid + remaining;
};

export const effectiveAgentPayable = (rec) => {
  const target = num(rec?.total_agent_commission_due ?? rec?.net_payable_to_agent);
  const paid = num(rec?.amount_paid_to_agent);
  const remaining = num(rec?.remaining_pay_to_agent);
  if (target > 0) return target;
  return paid + remaining;
};

/** Total pay-out due to vehicle owner and booking agent combined. */
export const effectiveTotalPayable = (rec) => {
  const payOwner = num(rec?.total_pay_to_owner_due ?? rec?.remaining_pay_to_owner);
  const recvComm = num(rec?.total_receive_from_owner_due ?? rec?.total_agent_commission_due);
  if (payOwner > 0) return payOwner;
  if (recvComm > 0) return recvComm;
  return effectiveOwnerPayable(rec) + effectiveAgentPayable(rec);
};

/** Legacy alias — collect from booking agent after commission retention. */
export const effectiveNetCollect = effectiveAgentRemit;

/** Legacy alias — combined owner + agent pay due. */
export const effectiveNetPayable = effectiveTotalPayable;

export const remainingAgentRemit = (rec) =>
  num(rec?.remaining_remit_from_agent ?? rec?.remaining_collect);

export const remainingOwnerPay = (rec) => num(rec?.remaining_pay_to_owner);

export const remainingAgentPay = (rec) => num(rec?.remaining_pay_to_agent);

export const remainingTotalPay = (rec) => {
  const pay = remainingOwnerPay(rec);
  const recv = num(rec?.remaining_receive_from_owner);
  const netPay = Math.max(pay - recv, 0);
  const netRecv = Math.max(recv - pay, 0);
  if (netPay > 0) return netPay;
  if (netRecv > 0) return netRecv;
  return remainingAgentPay(rec);
};

export const formatSettlementDirection = (direction) => {
  const map = {
    COLLECT_FROM_AGENT: 'Collect from Agent',
    COLLECT: 'Collect from Agent',
    PAY: 'Pay Out',
    SETTLED: 'Settled',
  };
  return map[direction] || direction || '—';
};

export const effectiveBalanceFreight = (rec) => {
  const basis = num(rec?.settlement_basis_balance_freight);
  if (basis > 0) return basis;
  const balance = num(rec?.balance_freight);
  if (balance > 0) return balance;
  const received = num(rec?.amount_received_from_agent ?? rec?.amount_received_from_owner);
  if (received > 0) return num(rec?.total_freight);
  return balance;
};

export const bookingAgentCount = (rec) =>
  (rec?.booked_agent_commission_json?.agents || []).length;

/** Vehicle owner freight share % (from owner profile / commission record). */
export const effectiveOwnerCommissionPercent = (rec, agent = null) => {
  const fromRecord = rec?.vehicle_owner_percentage ?? rec?.owner_commission_percent;
  if (fromRecord != null && fromRecord !== '') return parseFloat(fromRecord);
  const fromJson = rec?.booked_agent_commission_json?.owner_share_percent;
  if (fromJson != null && fromJson !== '') return parseFloat(fromJson);
  if (agent?.owner_share_percent != null && agent.owner_share_percent !== '') {
    return parseFloat(agent.owner_share_percent);
  }
  if (agent?.agent_commission_percent != null && agent.agent_commission_percent !== '') {
    return Math.max(0, 100 - parseFloat(agent.agent_commission_percent));
  }
  return null;
};

export const bookingAgentSummary = (rec) => {
  const agents = rec?.booked_agent_commission_json?.agents || [];
  if (!agents.length) return '—';
  const ownerPct = effectiveOwnerCommissionPercent(rec, agents[0]);
  const pctLabel = ownerPct != null && !Number.isNaN(ownerPct) ? ` (${ownerPct}% owner)` : '';
  if (agents.length === 1) return `${agents[0].agent_name}${pctLabel}`;
  return `${agents.length} agents${pctLabel}`;
};
